import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Check, ChevronLeft, ChevronRight, Loader2, ShoppingBag, X, Lock } from 'lucide-react';
import { useSidebar } from '../context/SidebarContext';
import { supabase } from '../lib/supabase';
import { postGenerateAd, GenerateAdError } from '../lib/generateAdRequest';
import { fetchStripeBillingPortalUrl } from '../lib/paymentServer';
import { gestionaPlanEnPortalStripe } from '../lib/gestionPlan';
import LoadingAnimation from '../components/LoadingAnimation';
import {
  ModeloPersonalizadoOption,
  ModeloGuardadoOption,
  EscenaPersonalizadaOption,
  EscenasGuardadasOption,
  InstruccionAdicionalOption,
} from '../components/opciones';
import type { ModeloPersonalizadoState } from '../components/opciones';

const STOCK_IMAGE = 'https://media.istockphoto.com/id/938226922/photo/young-stylish-man-in-eyeglasses-sitting-on-chair-isolated-on-grey.jpg?s=612x612&w=0&k=20&c=DtKUcN_L6jNHkGKbsfExszSUQVEVsRbOsc2d_vvc5qc=';

const videoPreviewMap: Record<string, string> = {
  vlog: '/images/tipos_de_videos/vlog.mp4',
  visitando_negocio: '/images/tipos_de_videos/visita_negocio.mp4',
  unboxing: '/images/tipos_de_videos/review.mp4',
  automovil: '/images/tipos_de_videos/auto.mp4',
  podcast: '/images/tipos_de_videos/podcast.mp4',
  entrevista: '/images/tipos_de_videos/entrevista.mp4',
  comparativa: '/images/tipos_de_videos/comparativa.mp4',
};

const videoStyleLabels: Record<string, string> = {
  vlog: 'Vlog',
  visitando_negocio: 'Visitando tu negocio',
  unboxing: 'Unboxing',
  automovil: 'Automóvil',
  podcast: 'Podcast',
  exterior: 'Exterior',
  entrevista: 'Entrevista',
  comparativa: 'Comparativa',
};

type ModeloMode = 'ia' | 'guardados' | 'personalizado';
type EscenaMode = 'automatica' | 'personalizada' | 'guardadas';

const ConfigurarVideoInfluencerDetalle: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const params = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const idsParam = params.get('ids') || '';
  const ids = useMemo(
    () => idsParam.split(',').map(s => s.trim()).filter(Boolean).filter(s => s !== '0'),
    [idsParam],
  );
  const styleId = params.get('style') || 'vlog';
  const styleLabel = videoStyleLabels[styleId] || styleId;

  const { sidebarWidth, setMobileTitleOverride, setMobileBackOverride } = useSidebar();

  const [modeloMode, setModeloMode] = useState<ModeloMode>('ia');
  const [escenaMode, setEscenaMode] = useState<EscenaMode>('automatica');
  const [instruccionAdicional, setInstruccionAdicional] = useState<string>('');

  // Menu open states
  const [showModeloGuardado, setShowModeloGuardado] = useState(false);
  const [showModeloPersonalizado, setShowModeloPersonalizado] = useState(false);
  const [showEscenaPersonalizada, setShowEscenaPersonalizada] = useState(false);
  const [showEscenasGuardadas, setShowEscenasGuardadas] = useState(false);
  const [showInstruccion, setShowInstruccion] = useState(false);

  // Option values
  const [selectedSavedModelId, setSelectedSavedModelId] = useState<number | null>(null);
  const [selectedSavedModelName, setSelectedSavedModelName] = useState<string | undefined>();
  const [modeloPersonalizado, setModeloPersonalizado] = useState<ModeloPersonalizadoState>({
    genero: '', edad: '', tipoCuerpo: '', tonoPiel: '', colorPelo: '', largoPelo: '',
  });
  const [escenaPrompt, setEscenaPrompt] = useState('');
  const [savedSceneId, setSavedSceneId] = useState<string | null>(null);
  const [savedSceneName, setSavedSceneName] = useState<string | undefined>();
  const [savedSceneFileName, setSavedSceneFileName] = useState<string | undefined>();

  // Product variants state
  type ProductVariant = { id: string; nombre: string; imageUrl: string | null };
  const [productVariants, setProductVariants] = useState<ProductVariant[]>([]);
  const [loadingProducts, setLoadingProducts] = useState(true);
  const productsFetchedRef = useRef(false);

  // Plan restriction state
  const [userPlan, setUserPlan] = useState<string>('gratis');
  // Destino del "mejorar plan": Billing Portal de Stripe (suscripción activa
  // afuera) o nuestro catálogo. Decide el medio de pago, no el país — ver
  // lib/gestionPlan.ts y ADR-661.
  const [enPortalStripe, setEnPortalStripe] = useState(false);
  const [showVideoPlanRestriction, setShowVideoPlanRestriction] = useState(false);
  const [openingBillingPortal, setOpeningBillingPortal] = useState(false);
  const canAccessVideoGeneration = userPlan === 'negocio' || userPlan === 'enterprise' || userPlan === 'enterprise1' || userPlan === 'enterprise2' || userPlan === 'enterprise3' || userPlan === 'enterprise_plus';

  // Fetch product variants on mount
  useEffect(() => {
    if (productsFetchedRef.current || ids.length === 0) { setLoadingProducts(false); return; }
    productsFetchedRef.current = true;

    const fetchProducts = async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) { setLoadingProducts(false); return; }
        const { data: profile } = await supabase.from('profiles').select('id_company').eq('id', user.id).single();
        const cId = (profile as any)?.id_company as string | undefined;
        if (!cId) { setLoadingProducts(false); return; }

        // Fetch plan + medio de pago
        const { data: companyData } = await supabase.from('company_profiles').select('plan, plataforma_pago').eq('id_company', cId).single();
        if (companyData?.plan) setUserPlan(companyData.plan);
        setEnPortalStripe(
          gestionaPlanEnPortalStripe(
            (companyData as any)?.plataforma_pago,
            (companyData as any)?.plan,
          ),
        );

        // Fetch variant names
        const { data: variantsData } = await supabase.from('variantes').select('id, nombre').in('id', ids);
        const variantMap: Record<string, string> = {};
        (variantsData || []).forEach((v: any) => { variantMap[String(v.id)] = String(v.nombre || 'Producto'); });

        // Fetch product images from productos_imagenes (same table as PruebaNuevaInterfaz)
        const { data: imagesData } = await supabase
          .from('productos_imagenes')
          .select('url, principal, variante_id')
          .eq('id_company', cId)
          .in('variante_id', ids)
          .order('principal', { ascending: false });

        // Group by variant, pick principal or first
        const imageByVariant: Record<string, string> = {};
        (imagesData || []).forEach((img: any) => {
          const vid = String(img.variante_id);
          if (!imageByVariant[vid]) imageByVariant[vid] = String(img.url || '');
        });

        // Sign URLs
        const results: ProductVariant[] = [];
        await Promise.all(ids.map(async (vid) => {
          const nombre = variantMap[vid] || 'Producto';
          const rawUrl = imageByVariant[vid] || '';
          let imageUrl: string | null = null;
          if (rawUrl) {
            try {
              const fileName = rawUrl.startsWith('http')
                ? (rawUrl.split('/').pop() || '').split('?')[0]
                : rawUrl;
              if (fileName) {
                const optPath = `${cId}/${vid}/opt/${fileName}`;
                const { data: optSigned } = await supabase.storage.from('productos').createSignedUrl(optPath, 3600);
                if (optSigned?.signedUrl) {
                  imageUrl = optSigned.signedUrl;
                } else {
                  const fullPath = `${cId}/${vid}/full/${fileName}`;
                  const { data: fullSigned } = await supabase.storage.from('productos').createSignedUrl(fullPath, 3600);
                  imageUrl = fullSigned?.signedUrl || (rawUrl.startsWith('http') ? rawUrl : null);
                }
              } else if (rawUrl.startsWith('http')) {
                imageUrl = rawUrl;
              }
            } catch {
              if (rawUrl.startsWith('http')) imageUrl = rawUrl;
            }
          }
          results.push({ id: vid, nombre, imageUrl });
        }));
        setProductVariants(results);
      } catch (e) {
        console.error('Error fetching product variants:', e);
      } finally {
        setLoadingProducts(false);
      }
    };
    fetchProducts();
  }, [ids]);

  // Generate state
  const [generating, setGenerating] = useState(false);
  const [genError, setGenError] = useState<string | null>(null);
  const generatingLockRef = useRef(false);
  const [showLoadingScreen, setShowLoadingScreen] = useState(false);
  const [completeProgressNow, setCompleteProgressNow] = useState(false);
  const willNavigateRef = useRef(false);
  const pendingNavRef = useRef<{ imageId: string; companyId: string } | null>(null);

  // Insufficient images modal state
  const [showInsufficientImagesModal, setShowInsufficientImagesModal] = useState(false);
  const [requiredImagesCount, setRequiredImagesCount] = useState(1);
  const [remainingImagesCount, setRemainingImagesCount] = useState(0);
  const [nextRenewalDate, setNextRenewalDate] = useState<Date | null>(null);

  // Hero animation state — same pattern as GeneratedImagesInline
  const heroRect = (location.state as any)?.heroRect as { top: number; left: number; width: number; height: number } | undefined;
  const desktopTargetRef = useRef<HTMLDivElement>(null);
  const mobileTargetRef = useRef<HTMLDivElement>(null);
  const [heroAnimating, setHeroAnimating] = useState(!!heroRect);
  const [heroPhase, setHeroPhase] = useState<'start' | 'end'>('start');
  const heroEndRect = useRef<{ top: number; left: number; width: number; height: number } | null>(null);
  const heroInitialized = useRef(false);
  const [contentReady, setContentReady] = useState(!heroRect);

  // Start content slide immediately (parallel with hero)
  useEffect(() => {
    if (heroRect && !contentReady) {
      requestAnimationFrame(() => requestAnimationFrame(() => setContentReady(true)));
    }
  }, []);  // eslint-disable-line react-hooks/exhaustive-deps

  // Scroll to top on mobile
  useEffect(() => {
    if (window.innerWidth < 640) window.scrollTo(0, 0);
  }, []);

  // Two-phase hero: mount at source rect, then measure target and transition
  useLayoutEffect(() => {
    if (!heroRect || heroInitialized.current) return;
    heroInitialized.current = true;

    // Double rAF: let layout settle, measure target, then trigger phase 'end'
    requestAnimationFrame(() => requestAnimationFrame(() => {
      const isMobile = window.innerWidth < 640;
      const target = isMobile ? mobileTargetRef.current : desktopTargetRef.current;
      if (!target) {
        setHeroAnimating(false);
        return;
      }
      const r = target.getBoundingClientRect();
      heroEndRect.current = { top: r.top, left: r.left, width: r.width, height: r.height };
      setHeroPhase('end');
      setTimeout(() => setHeroAnimating(false), 420);
    }));
  }, [heroRect]);

  useEffect(() => {
    setMobileTitleOverride(' ');
    return () => setMobileTitleOverride(null);
  }, [setMobileTitleOverride]);

  useEffect(() => {
    setMobileBackOverride(() => handleBack);
    return () => setMobileBackOverride(null);
  }, [setMobileBackOverride]);  // eslint-disable-line react-hooks/exhaustive-deps

  // For 'visitando_negocio', a saved scene is required
  const requiresSavedScene = styleId === 'visitando_negocio';
  const canGenerate = !generating && !(requiresSavedScene && !savedSceneFileName);

  const handleOpenBillingPortal = async () => {
    try {
      setOpeningBillingPortal(true);
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Usuario no autenticado');
      window.location.href = await fetchStripeBillingPortalUrl(user.id);
    } catch (err: any) {
      console.error('Error opening billing portal:', err);
      // El payment server sí distingue el motivo (ej. `no_stripe_customer`) —
      // mostrarlo, si no el botón parece que no hace nada. Mismo `alert` que
      // usan los otros handlers de portal (SubscriptionLimitModal, etc.).
      alert(err?.message || 'No se pudo abrir el portal de facturación.');
    } finally {
      setOpeningBillingPortal(false);
    }
  };

  const handleGenerate = async () => {
    if (generatingLockRef.current) return;
    generatingLockRef.current = true;

    // Plan restriction check
    if (!canAccessVideoGeneration) {
      setShowVideoPlanRestriction(true);
      generatingLockRef.current = false;
      return;
    }

    if (ids.length === 0) { generatingLockRef.current = false; return; }

    try {
      setGenError(null);
      setGenerating(true);
      setShowLoadingScreen(true);
      setCompleteProgressNow(false);

      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('No se encontró el usuario');

      const { data: profile } = await supabase
        .from('profiles')
        .select('id_company')
        .eq('id', user.id)
        .single();

      if (!profile?.id_company) throw new Error('No se encontró la empresa del usuario');
      const compId = profile.id_company as string;

      // Check image quota before generating
      const requiredImages = 1;
      const { data: companyProfile } = await supabase
        .from('company_profiles')
        .select('plan, imagenes_restantes, max_fotos_mes, paid_plan_start_date, upgrade_plan_date')
        .eq('id_company', compId)
        .single();

      const remaining = (companyProfile as any)?.imagenes_restantes as number | null | undefined;
      if (typeof remaining === 'number' && remaining < requiredImages) {
        setRequiredImagesCount(requiredImages);
        setRemainingImagesCount(remaining);
        const paidStart = (companyProfile as any)?.paid_plan_start_date as string | null;
        const upgradeDate = (companyProfile as any)?.upgrade_plan_date as string | null;
        if (paidStart) {
          const now = new Date();
          const ref = upgradeDate ? new Date(upgradeDate) : new Date(paidStart);
          const anchorDay = ref.getDate();
          const daysInMonth = (y: number, m: number) => new Date(y, m + 1, 0).getDate();
          const anchorFor = (y: number, m: number) => new Date(y, m, Math.min(anchorDay, daysInMonth(y, m)));
          let start = anchorFor(now.getFullYear(), now.getMonth());
          if (start.getTime() > now.getTime()) {
            const prev = new Date(now.getFullYear(), now.getMonth() - 1, 1);
            start = anchorFor(prev.getFullYear(), prev.getMonth());
          }
          const next = new Date(start.getFullYear(), start.getMonth() + 1, 1);
          setNextRenewalDate(anchorFor(next.getFullYear(), next.getMonth()));
        } else {
          setNextRenewalDate(new Date(new Date().getFullYear(), new Date().getMonth() + 1, 1));
        }
        setShowInsufficientImagesModal(true);
        setGenerating(false);
        setShowLoadingScreen(false);
        generatingLockRef.current = false;
        return;
      }

      const variantIds = ids.map(s => String(s).trim()).filter(s => !!s && s !== '0');
      if (variantIds.length === 0) throw new Error('No hay productos válidos seleccionados');

      // Build background mode
      let backgroundMode: 'ia' | 'descripcion' | 'guardado' = 'ia';
      let backgroundText: string | undefined;
      let backgroundFileName: string | undefined;

      if (escenaMode === 'personalizada' && escenaPrompt.trim()) {
        backgroundMode = 'descripcion';
        backgroundText = escenaPrompt.trim();
      } else if (escenaMode === 'guardadas' && savedSceneFileName) {
        backgroundMode = 'guardado';
        backgroundFileName = savedSceneFileName;
      }

      // Build request — same field names as PruebaNuevaInterfaz but with start_video_ugc mode
      const requestData: Record<string, any> = {
        user_id: user.id,
        variante_id: variantIds,
        mode: variantIds.length > 1 ? 'multiple' : 'single',
        modo: 'start_video_ugc',
        tipo_de_video: styleId,
        cantidad_imagenes: 1,
        Format: '9:16',
        new_ui: true,
        composicion: 'modelo',
        background_mode: backgroundMode,
        modelo_mode: modeloMode,
      };

      if (backgroundMode === 'descripcion' && backgroundText) {
        requestData.background_text = backgroundText;
      } else if (backgroundMode === 'guardado' && backgroundFileName) {
        requestData.background_file_name = backgroundFileName;
      }

      if (modeloMode === 'guardados' && selectedSavedModelId) {
        requestData.modelo_guardado_id = String(selectedSavedModelId);
        if (selectedSavedModelName) requestData.modelo_guardado_nombre = selectedSavedModelName;
      } else if (modeloMode === 'personalizado') {
        if (modeloPersonalizado.genero) requestData.genero = modeloPersonalizado.genero;
        if (modeloPersonalizado.edad) requestData.edad = modeloPersonalizado.edad;
        if (modeloPersonalizado.tipoCuerpo) requestData.tipo_cuerpo = modeloPersonalizado.tipoCuerpo;
        if (modeloPersonalizado.tonoPiel) requestData.tono_piel = modeloPersonalizado.tonoPiel;
        if (modeloPersonalizado.colorPelo) requestData.color_pelo = modeloPersonalizado.colorPelo;
        if (modeloPersonalizado.largoPelo) requestData.largo_pelo = modeloPersonalizado.largoPelo;
      }

      if (instruccionAdicional.trim()) {
        requestData.instruccion_adicional = instruccionAdicional.trim();
      }

      console.log('[ConfigurarVideoInfluencerDetalle] handleGenerate payload', requestData);

      // postGenerateAd rutea: flag ON → atlas `/v1/ads/generate` (JWT, con
      // execution_id + modo_utilizado 'video_ugc' inyectados por el router);
      // flag OFF → webhook V2 de n8n byte-idéntico. ADR-596.
      const data: any = await postGenerateAd(requestData);
      console.log('[ConfigurarVideoInfluencerDetalle] response', data);

      if (data?.response === 'success' && Array.isArray(data?.imagenes_generadas_ids) && data.imagenes_generadas_ids.length > 0) {
        const imageId = String(data.imagenes_generadas_ids[0]);
        const companyId = (data.id_company as string) || profile.id_company;
        willNavigateRef.current = true;
        pendingNavRef.current = { imageId, companyId };
        setCompleteProgressNow(true);
      } else {
        throw new Error('No se recibieron imágenes generadas.');
      }
    } catch (e: any) {
      console.error('[ConfigurarVideoInfluencerDetalle] handleGenerate error', e);
      // Accionable (ej. sin créditos, que con el atlas responde al instante en
      // vez de colgar 300s) → mensaje del backend tal cual.
      setGenError(
        e instanceof GenerateAdError && e.isActionable
          ? e.message
          : 'Se produjo un error. No se consumieron créditos. Por favor, volvé a intentarlo.',
      );
      setGenerating(false);
      setShowLoadingScreen(false);
      generatingLockRef.current = false;
    }
  };

  const handleBack = () => {
    const isMobile = window.innerWidth < 640;
    const target = isMobile ? mobileTargetRef.current : desktopTargetRef.current;
    const rect = target?.getBoundingClientRect();
    if (rect) {
      sessionStorage.setItem('influencer-reverse-hero', JSON.stringify({
        reverseHeroRect: { top: rect.top, left: rect.left, width: rect.width, height: rect.height },
        reverseStyleId: styleId,
      }));
    }
    navigate(-1);
  };

  const tapFlash = (e: React.MouseEvent<HTMLElement>) => {
    const el = e.currentTarget;
    el.classList.remove('tap-flash');
    void el.offsetWidth;
    el.classList.add('tap-flash');
  };

  // Card image content (no gradient overlay on detail page)
  const cardImageContent = videoPreviewMap[styleId] ? (
    <video
      src={videoPreviewMap[styleId]}
      muted
      autoPlay
      loop
      playsInline
      className="w-full h-full object-cover"
    />
  ) : (
    <img
      src={STOCK_IMAGE}
      alt={styleLabel}
      className="w-full h-full object-cover"
      loading="lazy"
    />
  );

  // Shared options form
  const optionsForm = (
    <div>
      {/* Product selection — Apple grouped list */}
      <div className="mb-6">
        <p className="text-[13px] font-normal text-[#3C3C43]/60 uppercase tracking-wide mb-2 px-4">
          {productVariants.length === 1 ? 'Producto seleccionado' : `${productVariants.length || 0} productos seleccionados`}
        </p>
        {loadingProducts ? (
          <div className="bg-white rounded-[13px] px-4 py-[11px]">
            <span className="text-[15px] text-brand-black/40">Cargando...</span>
          </div>
        ) : productVariants.length === 0 ? (
          <div className="bg-white rounded-[13px] px-4 py-[11px]">
            <span className="text-[15px] text-brand-black/40">No hay productos seleccionados</span>
          </div>
        ) : (
          <div className="bg-white rounded-[13px] overflow-hidden">
            {productVariants.map((v, i) => (
              <React.Fragment key={v.id}>
                {i > 0 && <div className="ml-[60px] h-px bg-black/[0.08]" />}
                <div className="flex items-center gap-3 px-4 py-[11px]">
                  <div className="shrink-0 w-10 h-10 rounded-[8px] overflow-hidden bg-black/[0.03]">
                    {v.imageUrl ? (
                      <img src={v.imageUrl} alt={v.nombre} className="w-full h-full object-cover" loading="lazy" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-brand-black/20">
                        <ShoppingBag className="w-4 h-4" />
                      </div>
                    )}
                  </div>
                  <span className="flex-1 min-w-0 text-[15px] text-brand-black/90 truncate">{v.nombre}</span>
                </div>
              </React.Fragment>
            ))}
          </div>
        )}
      </div>

      {/* Tipo de modelo */}
      <div>
        <p className="text-[13px] font-normal text-[#3C3C43]/60 uppercase tracking-wide mb-2 px-4">Tipo de modelo</p>
        <div className="rounded-[13px] bg-white overflow-hidden shadow-[0_0_0_0.5px_rgba(0,0,0,0.04)]">
          <button
            type="button"
            onClick={() => setModeloMode('ia')}
            onClickCapture={tapFlash}
            className="w-full px-4 py-[11px] text-[15px] text-brand-black/90 transition-colors flex items-center justify-between"
          >
            <span className="flex items-center gap-3">
              <span className={`material-symbols-rounded text-[20px] shrink-0 ${modeloMode === 'ia' ? 'text-[#1644EB]' : 'text-neutral-400'}`}>auto_awesome</span>
              Sugerido por IA
            </span>
            {modeloMode === 'ia' && <Check className="w-5 h-5 text-[#1644EB] shrink-0" />}
          </button>
          <div className="ml-[46px] h-px bg-black/[0.08]" />
          <button
            type="button"
            onClick={() => { setShowModeloGuardado(true); }}
            onClickCapture={tapFlash}
            className="w-full px-4 py-[11px] text-[15px] text-brand-black/90 transition-colors flex items-center justify-between"
          >
            <span className="flex items-center gap-3">
              <span className={`material-symbols-rounded text-[20px] shrink-0 ${modeloMode === 'guardados' ? 'text-[#1644EB]' : 'text-neutral-400'}`}>person</span>
              Modelo guardado
            </span>
            <span className="flex items-center gap-1 text-[15px] text-[#3C3C43]/30 min-w-0">
              <span className="truncate max-w-[120px]">
                {selectedSavedModelId ? (selectedSavedModelName || 'Seleccionado') : 'Seleccionar'}
              </span>
              {modeloMode === 'guardados' && <Check className="w-5 h-5 text-[#1644EB] shrink-0" />}
              {modeloMode !== 'guardados' && <ChevronRight className="w-[14px] h-[14px] text-[#C7C7CC] shrink-0" />}
            </span>
          </button>
          <div className="ml-[46px] h-px bg-black/[0.08]" />
          <button
            type="button"
            onClick={() => { setShowModeloPersonalizado(true); }}
            onClickCapture={tapFlash}
            className="w-full px-4 py-[11px] text-[15px] text-brand-black/90 transition-colors flex items-center justify-between"
          >
            <span className="flex items-center gap-3">
              <span className={`material-symbols-rounded text-[20px] shrink-0 ${modeloMode === 'personalizado' ? 'text-[#1644EB]' : 'text-neutral-400'}`}>edit</span>
              Personalizado
            </span>
            <span className="flex items-center gap-1">
              {modeloMode === 'personalizado' && <Check className="w-5 h-5 text-[#1644EB] shrink-0" />}
              <ChevronRight className="w-[14px] h-[14px] text-[#C7C7CC]" />
            </span>
          </button>
        </div>
      </div>

      {/* Escena — hidden for automovil, podcast, unboxing; only guardadas for visitando_negocio */}
      {!['automovil', 'podcast', 'unboxing'].includes(styleId) && (
      <div className="mt-6">
        <p className="text-[13px] font-normal text-[#3C3C43]/60 uppercase tracking-wide mb-2 px-4">Escena</p>
        <div className="rounded-[13px] bg-white overflow-hidden shadow-[0_0_0_0.5px_rgba(0,0,0,0.04)]">
          {/* Automática — hidden for visitando_negocio */}
          {styleId !== 'visitando_negocio' && (
          <>
          <button
            type="button"
            onClick={() => setEscenaMode('automatica')}
            onClickCapture={tapFlash}
            className="w-full px-4 py-[11px] text-[15px] text-brand-black/90 transition-colors flex items-center justify-between"
          >
            <span className="flex items-center gap-3">
              <span className={`material-symbols-rounded text-[20px] shrink-0 ${escenaMode === 'automatica' ? 'text-[#1644EB]' : 'text-neutral-400'}`}>auto_awesome</span>
              Automática
            </span>
            {escenaMode === 'automatica' && <Check className="w-5 h-5 text-[#1644EB] shrink-0" />}
          </button>
          <div className="ml-[46px] h-px bg-black/[0.08]" />
          </>
          )}
          {/* Escenas guardadas */}
          <button
            type="button"
            onClick={() => setShowEscenasGuardadas(true)}
            onClickCapture={tapFlash}
            className="w-full px-4 py-[11px] text-[15px] text-brand-black/90 transition-colors flex items-center justify-between"
          >
            <span className="flex items-center gap-3">
              <span className={`material-symbols-rounded text-[20px] shrink-0 ${escenaMode === 'guardadas' ? 'text-[#1644EB]' : 'text-neutral-400'}`}>bookmark</span>
              Escenas guardadas
            </span>
            <span className="flex items-center gap-1 text-[15px] text-[#3C3C43]/30 min-w-0">
              <span className="truncate max-w-[120px]">
                {savedSceneId ? (savedSceneName || 'Seleccionado') : 'Seleccionar'}
              </span>
              {escenaMode === 'guardadas' && <Check className="w-5 h-5 text-[#1644EB] shrink-0" />}
              {escenaMode !== 'guardadas' && <ChevronRight className="w-[14px] h-[14px] text-[#C7C7CC] shrink-0" />}
            </span>
          </button>
          {/* Personalizada — hidden for visitando_negocio */}
          {styleId !== 'visitando_negocio' && (
          <>
          <div className="ml-[46px] h-px bg-black/[0.08]" />
          <button
            type="button"
            onClick={() => setShowEscenaPersonalizada(true)}
            onClickCapture={tapFlash}
            className="w-full px-4 py-[11px] text-[15px] text-brand-black/90 transition-colors flex items-center justify-between"
          >
            <span className="flex items-center gap-3">
              <span className={`material-symbols-rounded text-[20px] shrink-0 ${escenaMode === 'personalizada' ? 'text-[#1644EB]' : 'text-neutral-400'}`}>brush</span>
              Personalizada
            </span>
            <span className="flex items-center gap-1">
              {escenaMode === 'personalizada' && <Check className="w-5 h-5 text-[#1644EB] shrink-0" />}
              <ChevronRight className="w-[14px] h-[14px] text-[#C7C7CC]" />
            </span>
          </button>
          </>
          )}
        </div>
        <p className="text-[13px] text-[#3C3C43]/60 px-4 mt-[6px]">
          {escenaMode === 'automatica'
            ? 'La IA elegirá automáticamente una escena adecuada para tu video.'
            : escenaMode === 'personalizada'
              ? 'Describí con texto la escena exacta que querés para tu video.'
              : escenaMode === 'guardadas'
                ? 'Usá una escena que ya tengas guardada en tu marca.'
                : 'Seleccioná un tipo de escena para tu video.'}
        </p>
      </div>
      )}

      {/* Instrucción adicional */}
      <div className="mt-6">
        <p className="text-[13px] font-normal text-[#3C3C43]/60 uppercase tracking-wide mb-2 px-4">Adicional</p>
        <div className="bg-white rounded-[13px] overflow-hidden shadow-[0_0_0_0.5px_rgba(0,0,0,0.04)]">
          <button
            type="button"
            onClick={() => setShowInstruccion(true)}
            onClickCapture={tapFlash}
            className="w-full px-4 py-[11px] text-[15px] text-brand-black/90 transition-colors flex items-center justify-between"
          >
            <span className="flex items-center gap-3">
              <span className={`material-symbols-rounded text-[20px] shrink-0 ${instruccionAdicional.trim() ? 'text-[#1644EB]' : 'text-neutral-400'}`}>edit_note</span>
              Instrucción adicional
            </span>
            <span className="flex items-center gap-1">
              {instruccionAdicional.trim() && <Check className="w-5 h-5 text-[#1644EB] shrink-0" />}
              <ChevronRight className="w-[14px] h-[14px] text-[#C7C7CC]" />
            </span>
          </button>
        </div>
        {instruccionAdicional.trim() ? (
          <p className="text-[13px] text-[#3C3C43]/60 px-4 mt-[6px] line-clamp-2">{instruccionAdicional}</p>
        ) : (
          <p className="text-[13px] text-[#3C3C43]/60 px-4 mt-[6px]">
            Agregá una instrucción extra para personalizar aún más la imagen inicial. Esta opción es opcional.
          </p>
        )}
      </div>

    </div>
  );

  return (
    <div className="bg-surface-mobile sm:bg-surface-content min-h-screen">
      {/* Desktop floating back button */}
      <div className="hidden sm:block fixed top-0 right-0 z-40" style={{ left: sidebarWidth }}>
        <div className="max-w-content mx-auto px-8 h-14 flex items-center">
          <button
            type="button"
            onClick={handleBack}
            className="inline-flex items-center gap-1 text-[#1644EB] hover:opacity-70 transition-opacity text-[15px] font-normal"
          >
            <ChevronLeft className="w-5 h-5" />
            <span>Atrás</span>
          </button>
        </div>
      </div>

      {/* Hero animation overlay — same pattern as GeneratedImagesInline */}
      {heroAnimating && heroRect && (() => {
        const isEnd = heroPhase === 'end';
        const spring = 'cubic-bezier(0.2, 0.9, 0.3, 1)';
        const transition = `top 400ms ${spring}, left 400ms ${spring}, width 400ms ${spring}, height 400ms ${spring}`;
        const end = heroEndRect.current;

        return (
          <div
            className="pointer-events-none"
            style={{
              position: 'fixed',
              zIndex: 50,
              overflow: 'hidden',
              borderRadius: 16,
              top: isEnd && end ? end.top : heroRect.top,
              left: isEnd && end ? end.left : heroRect.left,
              width: isEnd && end ? end.width : heroRect.width,
              height: isEnd && end ? end.height : heroRect.height,
              transition: isEnd ? transition : 'none',
              willChange: 'top, left, width, height',
            }}
          >
            {videoPreviewMap[styleId] ? (
              <video
                src={videoPreviewMap[styleId]}
                muted
                autoPlay
                loop
                playsInline
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  display: 'block',
                }}
              />
            ) : (
              <img
                src={STOCK_IMAGE}
                alt={styleLabel}
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  display: 'block',
                }}
              />
            )}
          </div>
        );
      })()}

      {/* Desktop layout: image left, options right */}
      <div className="hidden sm:flex max-w-content mx-auto px-8 pt-[72px] pb-6 gap-6">
        <div className="w-[400px] shrink-0 sticky top-[80px] self-start" style={{ opacity: heroAnimating ? 0 : 1 }}>
          <p className="text-[17px] font-semibold text-brand-black/90 mb-3">{styleLabel}</p>
          <div ref={desktopTargetRef}>
            <div className="relative rounded-lg overflow-hidden">
              <div className="aspect-[4/5] relative bg-neutral-100">
                {cardImageContent}
              </div>
            </div>
          </div>
        </div>
        <div
          className="flex-1 min-w-0"
          style={{
            marginTop: 36,
            transform: contentReady ? 'translateX(0)' : 'translateX(40px)',
            opacity: contentReady ? 1 : 0,
            transition: 'transform 0.35s cubic-bezier(0.25, 0.1, 0.25, 1), opacity 0.35s ease',
          }}
        >
          {optionsForm}
          <div className="flex items-center justify-between gap-4 mt-6">
            <p className="text-[13px] text-[#3C3C43]/60 flex-1 min-w-0">
              Se generará una imagen inicial como previsualización. A partir de esa imagen podrás generar el video final.
            </p>
            <button
              type="button"
              onClick={handleGenerate}
              disabled={!canGenerate}
              className={`inline-flex items-center justify-center gap-2 h-11 px-8 rounded-[10px] text-[15px] font-medium transition-all shrink-0 ${
                !canGenerate
                  ? 'bg-[#1644EB]/40 text-white/60 cursor-not-allowed'
                  : 'bg-[#1644EB] hover:bg-[#1238C8] active:bg-[#0F2FA8] text-white'
              }`}
            >
              {generating ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Generando...
                </>
              ) : 'Generar previsualización'}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile layout: image top, options below */}
      <div className="sm:hidden">
        {/* Card image at top with margins */}
        <div className="px-4 pt-4 flex justify-center" style={{ opacity: heroAnimating ? 0 : 1 }}>
          <div ref={mobileTargetRef} className="w-[calc(50%-4px)]">
            <div className="relative rounded-lg overflow-hidden">
              <div className="aspect-[4/5] relative bg-neutral-100">
                {cardImageContent}
              </div>
            </div>
          </div>
        </div>
        {/* Title below image */}
        <p className="text-[17px] font-semibold text-brand-black/90 text-center mt-2 px-4" style={{ opacity: heroAnimating ? 0 : 1 }}>{styleLabel}</p>

        {/* Options — slides up */}
        <div
          className="px-4 mt-3 pb-36"
          style={{
            transform: contentReady ? 'translateY(0)' : 'translateY(30px)',
            opacity: contentReady ? 1 : 0,
            transition: 'transform 0.35s cubic-bezier(0.25, 0.1, 0.25, 1), opacity 0.35s ease',
          }}
        >
          {optionsForm}
        </div>
      </div>

      {/* Mobile bottom bar */}
      <div className="sm:hidden fixed bottom-0 left-0 right-0 bg-white/72 backdrop-blur-xl border-t border-black/[0.06] px-4 py-3 z-30" style={{ paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 12px)' }}>
        <button
          type="button"
          onClick={handleGenerate}
          disabled={!canGenerate}
          className={`w-full h-[50px] rounded-[13px] text-[17px] font-semibold transition-colors ${
            !canGenerate
              ? 'bg-black/[0.06] text-[#3C3C43]/30 cursor-not-allowed'
              : 'bg-[#1644EB] active:bg-[#1238C8] text-white shadow-[0_2px_8px_rgba(22,68,235,0.25)]'
          }`}
        >
          {generating ? (
            <span className="inline-flex items-center gap-2">
              <Loader2 className="w-5 h-5 animate-spin" />
              Generando...
            </span>
          ) : 'Generar previsualización'}
        </button>
        <p className="text-[13px] text-[#3C3C43]/60 text-center mt-2">
          Se generará una imagen inicial como previsualización. A partir de esa imagen podrás generar el video final.
        </p>
      </div>

      {/* Option Sheets */}
      <ModeloGuardadoOption
        open={showModeloGuardado}
        onClose={() => setShowModeloGuardado(false)}
        selectedModelId={selectedSavedModelId}
        onSelect={(id, nombre) => {
          setSelectedSavedModelId(id);
          setSelectedSavedModelName(nombre);
          if (id) setModeloMode('guardados');
          setShowModeloGuardado(false);
        }}
      />

      <ModeloPersonalizadoOption
        open={showModeloPersonalizado}
        onClose={() => setShowModeloPersonalizado(false)}
        value={modeloPersonalizado}
        onChange={(v) => {
          setModeloPersonalizado(v);
          setModeloMode('personalizado');
        }}
        onAccept={(v) => {
          setModeloPersonalizado(v);
          setModeloMode('personalizado');
          setShowModeloPersonalizado(false);
        }}
      />

      <EscenaPersonalizadaOption
        open={showEscenaPersonalizada}
        onClose={() => setShowEscenaPersonalizada(false)}
        value={escenaPrompt}
        onChange={setEscenaPrompt}
        onAccept={(v) => {
          setEscenaPrompt(v);
          setEscenaMode('personalizada');
          setShowEscenaPersonalizada(false);
        }}
        variantIds={ids}
      />

      <EscenasGuardadasOption
        open={showEscenasGuardadas}
        onClose={() => setShowEscenasGuardadas(false)}
        selectedSceneId={savedSceneId}
        onSelect={(id, _nombreArchivo, _signedUrl, _nombre) => {
          setSavedSceneId(id);
          setSavedSceneName(_nombre || _nombreArchivo);
          setSavedSceneFileName(_nombreArchivo);
          if (id) setEscenaMode('guardadas');
          setShowEscenasGuardadas(false);
        }}
      />

      <InstruccionAdicionalOption
        open={showInstruccion}
        onClose={() => setShowInstruccion(false)}
        value={instruccionAdicional}
        onChange={setInstruccionAdicional}
        onAccept={(v) => {
          setInstruccionAdicional(v);
          setShowInstruccion(false);
        }}
      />

      {/* Loading Animation */}
      {showLoadingScreen && (
        <LoadingAnimation
          message="Generando imagen..."
          subMessage="Esto puede tardar unos segundos."
          durationMs={35000}
          completeNow={completeProgressNow}
          onCompleted={() => {
            setShowLoadingScreen(false);
            setGenerating(false);
            generatingLockRef.current = false;
            if (willNavigateRef.current && pendingNavRef.current) {
              willNavigateRef.current = false;
              const { imageId, companyId } = pendingNavRef.current;
              pendingNavRef.current = null;
              const qp = new URLSearchParams();
              if (ids.length > 0) qp.set('ids', ids.join(','));
              qp.set('style', styleId);
              qp.set('imageId', String(imageId));
              qp.set('companyId', companyId);
              navigate(`/redes-sociales/crear-anuncio/configurar-video-influencer-video?${qp.toString()}`);
            }
          }}
        />
      )}

      {/* Insufficient Images Modal */}
      {showInsufficientImagesModal && (
        <div className="fixed inset-0 bg-brand-black/60 backdrop-blur-sm z-[9999] flex items-center justify-center">
          <div className="bg-surface-card rounded-lg shadow-md w-full max-w-xl mx-4">
            <div className="p-5">
              <h3 className="text-[17px] font-semibold text-brand-black/90 mb-3">
                Imágenes insuficientes para generar
              </h3>
              <p className="text-[15px] text-brand-black/90 mb-2">
                Para generar necesitás <strong>{requiredImagesCount} {requiredImagesCount === 1 ? 'crédito' : 'créditos'}</strong>, pero {remainingImagesCount === 0 ? <>no te quedan créditos este mes</> : <>solo tenés <strong>{remainingImagesCount}</strong> créditos restantes este mes</>}.
              </p>
              {nextRenewalDate && (
                <p className="text-[15px] text-neutral-500 mb-2">
                  Tu cupo de imágenes se renovará el {nextRenewalDate.toLocaleDateString('es-AR', { day: 'numeric', month: 'long', year: 'numeric' })}.
                </p>
              )}
              <p className="text-[15px] text-brand-black/90 mb-6">
                Para generar ya mismo, podés mejorar tu plan.
              </p>
              <div className="flex flex-col sm:flex-row gap-2.5 items-center justify-center sm:justify-end">
                <button
                  type="button"
                  onClick={() => { setShowInsufficientImagesModal(false); navigate('/subscription'); }}
                  className="inline-flex items-center justify-center text-center h-10 px-4 w-full sm:w-auto bg-brand-primary hover:bg-brand-primary-hover text-white text-[15px] font-medium rounded-md shadow-sm transition-colors"
                >
                  Mejorar mi plan
                </button>
                <button
                  type="button"
                  onClick={() => setShowInsufficientImagesModal(false)}
                  className="inline-flex items-center justify-center text-center h-10 px-4 w-full sm:w-auto border border-neutral-200 bg-surface-card text-brand-black/90 text-[15px] font-medium rounded-md shadow-sm hover:bg-neutral-100 transition-colors"
                >
                  Cancelar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Error toast */}
      {genError && (
        <div className="fixed bottom-24 sm:bottom-8 left-4 right-4 sm:left-auto sm:right-4 sm:w-96 bg-white rounded-[13px] shadow-[0_8px_40px_rgba(0,0,0,0.12),0_0_0_0.5px_rgba(0,0,0,0.06)] p-4 z-50">
          <div className="flex items-start gap-3">
            <span className="material-symbols-rounded text-[22px] text-[#FF3B30] shrink-0">error</span>
            <div className="flex-1 min-w-0">
              <p className="text-[15px] font-semibold text-brand-black/90">Error al generar</p>
              <p className="text-[13px] text-[#3C3C43]/60 mt-0.5">{genError}</p>
            </div>
            <button type="button" onClick={() => setGenError(null)} className="text-[#3C3C43]/30 hover:text-[#3C3C43]/60 transition-colors shrink-0 -mt-0.5">
              <span className="material-symbols-rounded text-[18px]">close</span>
            </button>
          </div>
        </div>
      )}
      {/* Video plan restriction modal */}
      {showVideoPlanRestriction && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-end sm:items-center sm:justify-center sm:p-4">
          <div className="bg-surface-card w-full h-full sm:h-auto sm:max-w-2xl sm:rounded-lg shadow-md relative flex flex-col sm:flex-row sm:overflow-hidden">
            <button type="button" onClick={() => setShowVideoPlanRestriction(false)} className="absolute top-3 right-3 z-10 w-8 h-8 rounded-sm text-neutral-400 hover:text-neutral-600 hover:bg-neutral-100 inline-flex items-center justify-center transition-colors" aria-label="Cerrar"><X className="w-5 h-5" /></button>
            <h3 className="sm:hidden text-[17px] font-semibold text-brand-black/90 text-center pt-16 pb-4 px-5">Función exclusiva</h3>
            <div className="relative flex-shrink-0 px-5 sm:px-0 sm:w-1/2">
              <video src="/images/video_animar_imagen.mp4" autoPlay loop muted playsInline className="w-full max-w-[280px] mx-auto sm:max-w-none aspect-[4/5] object-cover rounded-md sm:rounded-none sm:rounded-l-lg" />
              <div className="absolute -bottom-3 right-8 w-12 h-12 bg-brand-primary rounded-full flex items-center justify-center shadow-md sm:hidden"><Lock className="w-5 h-5 text-white" /></div>
            </div>
            <div className="flex-1 flex flex-col justify-center p-5 pt-8 pb-8 sm:p-6">
              <h3 className="hidden sm:block text-[17px] font-semibold text-brand-black/90 text-center mb-3">Función exclusiva</h3>
              <p className="text-[15px] text-neutral-500 mb-5 sm:text-center">La generación de videos está disponible únicamente para los usuarios de los planes <span className="font-semibold text-brand-black/80">Pro</span> y <span className="font-semibold text-brand-black/80">Ultra</span>.</p>
              <div className="flex flex-col gap-2 mt-auto pb-4 sm:mt-0 sm:pb-0">
                {!enPortalStripe ? (
                  <button type="button" onClick={() => { setShowVideoPlanRestriction(false); navigate('/subscription'); }} className="inline-flex items-center justify-center w-full h-10 bg-brand-primary hover:bg-brand-primary-hover text-white rounded-md text-[15px] font-medium shadow-sm transition-colors">{userPlan === 'gratis' ? 'Ver planes' : 'Mejorar plan'}</button>
                ) : (
                  <button type="button" onClick={handleOpenBillingPortal} disabled={openingBillingPortal} className="inline-flex items-center justify-center w-full h-10 bg-brand-primary hover:bg-brand-primary-hover disabled:opacity-70 text-white rounded-md text-[15px] font-medium shadow-sm transition-colors">{openingBillingPortal ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Mejorar plan'}</button>
                )}
                <button type="button" onClick={() => setShowVideoPlanRestriction(false)} className="inline-flex items-center justify-center w-full h-10 bg-surface-card hover:bg-neutral-100 text-neutral-600 border border-neutral-200 rounded-md text-[15px] font-medium shadow-sm transition-colors">Cancelar</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ConfigurarVideoInfluencerDetalle;
